import React, { useState, useEffect } from 'react';
import { Plus, Edit3, Trash2, Eye, EyeOff, Save, X, FileText } from 'lucide-react';

interface Post {
  id: string; title: string; content: string; type: string;
  status: string; authorName: string; imageUrl: string;
  createdAt: string; updatedAt: string;
}
interface Props { villageId: string; villageName: string; }

const POST_TYPES = [
  { value: 'NEWS',           label: '📰 News' },
  { value: 'ANNOUNCEMENT',   label: '📢 Announcement' },
  { value: 'CULTURAL_FACT',  label: '🏺 Cultural Fact' },
  { value: 'EVENT_REPORT',   label: '🎉 Event Report' },
  { value: 'HERITAGE_STORY', label: '📜 Heritage Story' },
];
const EMPTY_POST = { title: '', content: '', type: 'NEWS', status: 'DRAFT', authorName: '', imageUrl: '' };

const CommunityPostManager: React.FC<Props> = ({ villageId, villageName }) => {
  const [posts, setPosts]       = useState<Post[]>([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId]     = useState<string | null>(null);
  const [form, setForm]         = useState({ ...EMPTY_POST });
  const [error, setError]       = useState('');

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const r = await fetch(`/api/community-portals/${villageId}/posts`);
      const d = await r.json();
      if (d.success) setPosts(d.posts || []);
    } catch { setError('Failed to load posts'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchPosts(); }, [villageId]);

  const openNew = () => { setEditId(null); setForm({ ...EMPTY_POST }); setShowForm(true); setError(''); };
  const openEdit = (p: Post) => {
    setEditId(p.id);
    setForm({ title: p.title, content: p.content, type: p.type,
              status: p.status, authorName: p.authorName, imageUrl: p.imageUrl });
    setShowForm(true); setError('');
  };
  const cancel = () => { setShowForm(false); setEditId(null); setError(''); };

  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) { setError('Title and content are required.'); return; }
    setSaving(true); setError('');
    try {
      const url    = editId ? `/api/community-portals/${villageId}/posts/${editId}` : `/api/community-portals/${villageId}/posts`;
      const method = editId ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      const d = await r.json();
      if (d.success) { await fetchPosts(); cancel(); } else setError(d.error || 'Save failed');
    } catch { setError('Network error'); }
    finally { setSaving(false); }
  };

  const deletePost = async (id: string, title: string) => {
    if (!confirm(`Delete post: "${title}"?`)) return;
    try {
      const r = await fetch(`/api/community-portals/${villageId}/posts/${id}`, { method: 'DELETE' });
      const d = await r.json();
      if (d.success) await fetchPosts(); else setError(d.error || 'Delete failed');
    } catch { setError('Network error'); }
  };

  const toggleStatus = async (p: Post) => {
    const newStatus = p.status === 'PUBLISHED' ? 'DRAFT' : 'PUBLISHED';
    try {
      const r = await fetch(`/api/community-portals/${villageId}/posts/${p.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...p, status: newStatus }),
      });
      const d = await r.json();
      if (d.success) await fetchPosts();
    } catch { setError('Network error'); }
  };

  const statusColor = (s: string) =>
    s === 'PUBLISHED' ? 'text-green-400 bg-green-900/30' :
    s === 'DRAFT'     ? 'text-yellow-400 bg-yellow-900/30' :
                        'text-slate-400 bg-slate-700/30';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Posts & News</h3>
          <p className="text-sm text-slate-400">{villageName} · {posts.length} post{posts.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={openNew}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[#D4AF37] text-slate-900 hover:bg-[#c49b2f] transition-colors">
          <Plus className="w-4 h-4" /> New Post
        </button>
      </div>

      {error && <div className="bg-red-900/30 border border-red-700 rounded-lg p-3 text-red-300 text-sm">{error}</div>}

      {showForm && (
        <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-5 space-y-4">
          <h4 className="font-semibold text-[#D4AF37]">{editId ? '✏️ Edit Post' : '➕ New Post'}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-sm text-slate-300 mb-1 block">Title *</label>
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Post title..."
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
            </div>
            <div>
              <label className="text-sm text-slate-300 mb-1 block">Type</label>
              <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]">
                {POST_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm text-slate-300 mb-1 block">Status</label>
              <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]">
                <option value="DRAFT">📝 Draft</option>
                <option value="PUBLISHED">🌐 Published</option>
                <option value="ARCHIVED">📦 Archived</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-slate-300 mb-1 block">Author Name</label>
              <input value={form.authorName} onChange={e => setForm(f => ({ ...f, authorName: e.target.value }))}
                placeholder="e.g. Community Secretariat"
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
            </div>
            <div>
              <label className="text-sm text-slate-300 mb-1 block">Image URL (optional)</label>
              <input value={form.imageUrl} onChange={e => setForm(f => ({ ...f, imageUrl: e.target.value }))}
                placeholder="https://..."
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm text-slate-300 mb-1 block">Content *</label>
              <textarea rows={6} value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                placeholder="Write your post content here..."
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37] resize-y" />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={save} disabled={saving}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium bg-[#0F5132] text-white hover:bg-[#0d4429] disabled:opacity-50 transition-colors">
              <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save Post'}
            </button>
            <button onClick={cancel}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm border border-slate-600 text-slate-300 hover:bg-slate-700 transition-colors">
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-slate-400 text-sm py-8 text-center">Loading posts…</div>
      ) : posts.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-slate-700 rounded-xl">
          <FileText className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">No posts yet. Create your first post.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map(p => (
            <div key={p.id}
              className="bg-slate-800/40 border border-slate-700 rounded-xl p-4 flex items-start gap-4 hover:border-slate-600 transition-colors">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor(p.status)}`}>{p.status}</span>
                  <span className="text-xs text-slate-500">{POST_TYPES.find(t => t.value === p.type)?.label ?? p.type}</span>
                </div>
                <p className="text-white font-medium text-sm truncate">{p.title}</p>
                <p className="text-slate-400 text-xs mt-1 line-clamp-2">{p.content}</p>
                {p.authorName && <p className="text-slate-500 text-xs mt-1">by {p.authorName}</p>}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => toggleStatus(p)} title={p.status === 'PUBLISHED' ? 'Unpublish' : 'Publish'}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-[#D4AF37] hover:bg-slate-700 transition-colors">
                  {p.status === 'PUBLISHED' ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button onClick={() => openEdit(p)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-blue-400 hover:bg-slate-700 transition-colors">
                  <Edit3 className="w-4 h-4" />
                </button>
                <button onClick={() => deletePost(p.id, p.title)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-slate-700 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
export default CommunityPostManager;
